// ---------------------------------------------------------------------------
// What a word means, asked for one already-revealed word at a time.
//
// The server's rule is in `packages/backend/src/definitions.ts` and it is the
// only thing keeping this from being a validity oracle: the endpoint takes a
// word AND the place that word was already shown, and it refuses everything
// else with a 403 that reads the same for "not in that round", "not revealed
// yet" and "not a word". Nothing here is allowed to make that gate look like a
// suggestion — this module never asks about a word the screen is not already
// showing, and it never reports what a refusal meant.
//
// **A 403 is not an error and is never shown to anybody.** It is the answer to
// a question the UI should not have asked, and the only correct response to it
// is silence. So is a 404, a 400, a dropped connection, and a 200 whose
// `definition` is null — which is the ordinary case rather than the exceptional
// one: 94.5% of the common word list has a gloss and 35.7% of the full
// validation list does, so most of the obscure end of a board has nothing to
// say and the screen says nothing. See DESIGN, Definitions.
//
// **`request` in `api.ts` is deliberately not used.** It throws on any non-2xx,
// which is right for a page that has to tell somebody their game did not load
// and wrong here, where the most likely non-2xx is the ordinary one.
//
// ## The cache
//
// One map for the life of the page, keyed on game, round and normalized word.
// A player moving around a reveal or a record page asks for the same word over
// and over — a pointer crossing a row, a tab pass through a table — and the
// answer cannot change: the endpoint sets `cache-control: public, max-age=3600`
// for exactly that reason, and a definition of a word from a finished round is
// final. The PROMISE is what is stored rather than the value, so two components
// asking at once make one request between them.
//
// Refusals and empty answers are cached too, and that is the point rather than
// an oversight: a word with no gloss is the common case, and re-asking on every
// hover would spend a request per pointer move to be told nothing again.
// ---------------------------------------------------------------------------

import { normalizeWord } from '@countdown/shared';

/**
 * The gloss, as a screen needs it.
 *
 * `lemma` is the word the gloss actually defines and it is not always the word
 * asked about: AAHED carries AAH's meaning and ACIDLY carries ACID's. It is on
 * the response so the screen can say which one it is showing — presenting one
 * word's meaning under another word's name is the thing the field exists to
 * stop. `source` is on the wire and is not here: whether a gloss came from the
 * bundled file or from the fallback API is a fact about this server, not about
 * the word, and no screen may draw it.
 */
export interface WordDefinition {
  lemma: string;
  partOfSpeech: string | null;
  gloss: string;
}

/** The shape the endpoint answers with, as much of it as this file reads. */
interface DefinitionPayload {
  definition?: {
    lemma?: unknown;
    partOfSpeech?: unknown;
    gloss?: unknown;
  } | null;
}

/**
 * Whether the gloss defines a DIFFERENT word than the one asked about.
 *
 * Compared through `normalizeWord`, which is what the endpoint matched the word
 * with in the first place, so a difference here is a real redirect to another
 * entry — AAHED to AAH, ACIDLY to ACID — rather than a difference of case or of
 * an accent. Whichever screen draws the gloss has to say so when this is true.
 */
export function definesAnotherWord(word: string, definition: WordDefinition): boolean {
  const lemma = normalizeWord(definition.lemma);
  return lemma.length > 0 && lemma !== normalizeWord(word);
}

const cache = new Map<string, Promise<WordDefinition | null>>();

export function definitionCacheKey(gameId: string, roundIndex: number, word: string): string {
  return `${gameId} ${roundIndex} ${normalizeWord(word)}`;
}

/**
 * Only for tests, which would otherwise inherit whatever the file before them
 * asked for. `useSiteConfig`'s module-level cache is the cautionary tale in
 * CLAUDE.md: a cache that outlives a test makes the order tests run in decide
 * whether they pass.
 */
export function resetDefinitionCache(): void {
  cache.clear();
}

/**
 * Read a definition out of whatever the endpoint sent, or null.
 *
 * Written as a parse rather than a cast because one of this module's callers
 * hands `fetch` a URL and gets back whatever a proxy, an error page or a stubbed
 * global felt like returning. A body without a usable `gloss` is nothing to say,
 * which is the same outcome as a 403, and both render as nothing.
 */
function readDefinition(body: unknown): WordDefinition | null {
  if (typeof body !== 'object' || body === null) return null;
  const definition = (body as DefinitionPayload).definition;
  if (typeof definition !== 'object' || definition === null) return null;

  const gloss = typeof definition.gloss === 'string' ? definition.gloss.trim() : '';
  if (gloss.length === 0) return null;

  const lemma = typeof definition.lemma === 'string' ? definition.lemma : '';
  return {
    lemma,
    partOfSpeech:
      typeof definition.partOfSpeech === 'string' && definition.partOfSpeech.length > 0
        ? definition.partOfSpeech
        : null,
    gloss,
  };
}

async function fetchDefinition(
  gameId: string,
  roundIndex: number,
  word: string,
): Promise<WordDefinition | null> {
  try {
    const response = await fetch(
      `/api/games/${encodeURIComponent(gameId)}/rounds/${roundIndex}/definition` +
        `?word=${encodeURIComponent(word)}`,
    );
    // Every refusal is the same nothing. A 403 means the UI asked about a word
    // the round did not put on the screen, which is a bug in the caller and
    // still not something to put in front of a player.
    if (!response.ok) return null;
    return readDefinition((await response.json()) as unknown);
  } catch {
    return null;
  }
}

/**
 * The definition of one revealed word, or null when there is nothing to show.
 *
 * Never rejects. Every failure — refused, missing, malformed, offline — is the
 * same null, because there is exactly one thing a caller can do with any of
 * them and it is to draw nothing.
 *
 * `gameId` may be null, which is a real state rather than a caller's mistake:
 * `ClientGameState.gameId` is the id of a game's written RECORD and is null for
 * the whole of a live game. With no id there is no question to ask, so no
 * request is made at all.
 */
export function lookupDefinition(
  gameId: string | null,
  roundIndex: number,
  word: string,
): Promise<WordDefinition | null> {
  const normalized = normalizeWord(word);
  if (gameId === null || gameId.length === 0) return Promise.resolve(null);
  // The endpoint's own bounds. Asking outside them can only ever be refused, so
  // the round trip is spent to be told nothing.
  if (normalized.length === 0 || normalized.length > 9) return Promise.resolve(null);
  if (!Number.isInteger(roundIndex) || roundIndex < 0) return Promise.resolve(null);

  const key = definitionCacheKey(gameId, roundIndex, normalized);
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const pending = fetchDefinition(gameId, roundIndex, normalized);
  cache.set(key, pending);
  return pending;
}
